'use client';

import { useState, useEffect } from 'react';

interface AudioBookProgressModalProps {
  isGenerating: boolean;
  audioBookId: string | null;
}

export default function AudioBookProgressModal({
  isGenerating,
  audioBookId,
}: AudioBookProgressModalProps) {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('Initializing your audio picture book...');
  const [title, setTitle] = useState('');
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState('4-6 minutes');

  useEffect(() => {
    if (!isGenerating || !audioBookId) {
      setProgress(0);
      setCurrentStep('Initializing your audio picture book...');
      setTitle('');
      return;
    }

    const startTime = Date.now();
    
    // Much more realistic progress simulation based on actual generation time
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const elapsedSeconds = (Date.now() - startTime) / 1000;
        
        // Multi-phase speed adjustment for realistic progress
        let increment;
        if (prev < 15) {
          // Fast start (0-15%): Initial setup
          increment = Math.random() * 4 + 3; // 3-7% per tick
        } else if (prev < 40) {
          // Medium speed (15-40%): Story generation
          increment = Math.random() * 3 + 1.5; // 1.5-4.5% per tick
        } else if (prev < 65) {
          // Slower (40-65%): Image generation (takes longest)
          increment = Math.random() * 2 + 0.8; // 0.8-2.8% per tick
        } else if (prev < 85) {
          // Slow (65-85%): Audio generation
          increment = Math.random() * 1.5 + 0.5; // 0.5-2% per tick
        } else if (prev < 95) {
          // Very slow (85-95%): Final processing
          increment = Math.random() * 0.8 + 0.2; // 0.2-1% per tick
        } else {
          // Crawl (95-98%): Waiting for completion
          increment = Math.random() * 0.3 + 0.1; // 0.1-0.4% per tick
        }
        
        // Cap at 98% to leave room for real completion signal
        const newProgress = Math.min(prev + increment, 98);
        
        // Update step labels based on progress
        if (newProgress >= 5 && newProgress < 20) {
          setCurrentStep('🎨 Crafting your magical story...');
        } else if (newProgress >= 20 && newProgress < 45) {
          setCurrentStep('🖼️ Creating beautiful illustrations...');
        } else if (newProgress >= 45 && newProgress < 50) {
          setCurrentStep('🌳 Adding 3D objects and effects...');
        } else if (newProgress >= 50 && newProgress < 72) {
          setCurrentStep('🎙️ Recording professional narration...');
        } else if (newProgress >= 72 && newProgress < 85) {
          setCurrentStep('🎵 Adding ambient sound effects...');
        } else if (newProgress >= 85 && newProgress < 93) {
          setCurrentStep('✨ Adding magical page effects...');
        } else if (newProgress >= 93 && newProgress < 98) {
          setCurrentStep('🎁 Wrapping up your masterpiece...');
        }
        
        // Dynamic time estimation based on actual progress and elapsed time
        if (newProgress > 10 && newProgress < 98) {
          const progressRate = newProgress / elapsedSeconds; // % per second
          const remainingProgress = 100 - newProgress;
          const estimatedSecondsLeft = remainingProgress / progressRate;
          
          if (estimatedSecondsLeft < 60) {
            setEstimatedTimeRemaining('Less than 1 minute');
          } else if (estimatedSecondsLeft < 120) {
            setEstimatedTimeRemaining('About 1-2 minutes');
          } else if (estimatedSecondsLeft < 180) {
            setEstimatedTimeRemaining('About 2-3 minutes');
          } else if (estimatedSecondsLeft < 240) {
            setEstimatedTimeRemaining('About 3-4 minutes');
          } else if (estimatedSecondsLeft < 300) {
            setEstimatedTimeRemaining('About 4-5 minutes');
          } else {
            setEstimatedTimeRemaining('About 5-6 minutes');
          }
        }
        
        return newProgress;
      });
    }, 1000); // Update every second for smooth, realistic progress

    // Poll status more frequently and boost progress when needed
    let pollCount = 0;
    const statusInterval = setInterval(async () => {
      pollCount++;
      try {
        const res = await fetch(`/api/audio-book/status?id=${audioBookId}`);
        if (res.ok) {
          const data = await res.json();
          const elapsedSeconds = (Date.now() - startTime) / 1000;
          
          console.log(`[Progress Modal] Poll #${pollCount} at ${elapsedSeconds.toFixed(1)}s: ${data.status}, Progress: ${progress.toFixed(1)}%`);
          
          if (data.title && data.title !== 'Generating...') {
            setTitle(data.title);
          }
          
          // Smart progress boost: If we're polling for a long time and progress is low, speed it up
          if (pollCount > 15 && progress < 60) {
            console.log(`[Progress Modal] ⚡ Long generation detected (${elapsedSeconds.toFixed(0)}s), boosting progress`);
            setProgress(prev => Math.min(prev + 5, 75)); // Jump ahead but cap at 75%
          }
          
          if (data.status === 'completed') {
            console.log('[Progress Modal] ✅ Book completed! Redirecting...');
            setProgress(100);
            setCurrentStep('🎉 Your audio picture book is ready!');
            clearInterval(progressInterval);
            clearInterval(statusInterval);
            
            // Small delay before redirect to show completion
            setTimeout(() => {
              window.location.href = `/audio-books/${audioBookId}`;
            }, 1200);
          } else if (data.status === 'failed') {
            console.error('[Progress Modal] ❌ Generation failed');
            setCurrentStep('❌ Generation failed. Please try again.');
            setProgress(0);
            clearInterval(progressInterval);
            clearInterval(statusInterval);
          } else if (data.status === 'pending') {
            console.log(`[Progress Modal] Still pending after ${elapsedSeconds.toFixed(0)}s...`);
          }
        } else {
          console.warn(`[Progress Modal] Status fetch failed: ${res.status}`);
        }
      } catch (error) {
        console.error('[Progress Modal] Status fetch error:', error);
      }
    }, 2000); // Poll every 2 seconds

    return () => {
      clearInterval(progressInterval);
      clearInterval(statusInterval);
    };
  }, [isGenerating, audioBookId]);

  if (!isGenerating) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '2rem',
    }}>
      <div style={{
        background: 'var(--color-bg-primary)',
        borderRadius: 'var(--radius-xl)',
        padding: '3rem 2rem',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      }}>
        {/* Loading Video */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '2rem',
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            style={{
              width: '100%',
              maxWidth: '300px',
              height: 'auto',
              display: 'block',
            }}
            onLoadStart={() => console.log('Video loading started')}
            onCanPlay={() => console.log('Video can play')}
            onError={(e) => console.error('Video load error:', e)}
          >
            <source
              src="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/loading-audio-book.mp4"
              type="video/mp4"
            />
            Your browser does not support video playback.
          </video>
        </div>

        {/* Title */}
        {title && (
          <h2 style={{
            fontSize: '1.5rem',
            textAlign: 'center',
            marginBottom: '1rem',
            color: 'var(--color-primary)',
          }}>
            {title}
          </h2>
        )}

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '8px',
          background: 'var(--color-border)',
          borderRadius: '4px',
          overflow: 'hidden',
          marginBottom: '1rem',
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #A8D8E6 0%, #C9B6E4 50%, #F8C8A0 100%)',
            transition: 'width 0.5s ease',
          }} />
        </div>

        {/* Progress Percentage */}
        <div style={{
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'var(--color-text-secondary)',
          marginBottom: '1.5rem',
        }}>
          {Math.round(progress)}%
        </div>

        {/* Current Step */}
        <p style={{
          textAlign: 'center',
          fontSize: '1.125rem',
          color: 'var(--color-text-primary)',
          marginBottom: '1rem',
        }}>
          {currentStep}
        </p>

        {/* Estimated Time */}
        <p style={{
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'var(--color-text-tertiary)',
        }}>
          {progress < 98 
            ? `Estimated time remaining: ${estimatedTimeRemaining} ✨`
            : 'Almost done! Just a few more seconds... 🎉'
          }
        </p>
      </div>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

